import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../../axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingScreen from "../../components/LoadingScreen";
import getDecryptData from "../../../helpers/getDecryptData";
import { useThemeContext } from "../../../fetchdata/ThemeContext";
import { SchedulePickerToast } from "../../components/SchedulePickerToast";

const DoctorQueeView = () => {
  const { sendMessage, allMessages } = useThemeContext();

  const [currentDate, setCurrentDate] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [currentMinutes, setCurrentMinutes] = useState(null);

  const [Quee, setQuee] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { hospital } = useParams();
  const navigate = useNavigate();

  // ✅ doctorId state (async)
  const [doctorId, setDoctorId] = useState(null);

  // -----------------------------
  // Helpers
  // -----------------------------
  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const toDateOnly = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const getMyProductIndex = (order, dId) => {
    if (!order?.addProduct?.length || !dId) return -1;

    return order.addProduct.findIndex((p) => {
      // employeeId may be string or populated object
      if (p?.employeeId) {
        if (typeof p.employeeId === "string" && p.employeeId === dId) return true;
        if (typeof p.employeeId === "object" && p.employeeId?._id === dId) return true;
      }
      if (p?.employee?.value && p.employee.value === dId) return true;
      return false;
    });
  };

  const makeKey = (orderId, dId, productIndex) =>
    `${orderId || ""}::${dId || ""}::${productIndex ?? ""}`;

  // -----------------------------
  // Date/Time ticker
  // -----------------------------
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      const dateOptions = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      };
      const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

      setCurrentDate(now.toLocaleDateString(undefined, dateOptions));
      setCurrentTime(now.toLocaleTimeString(undefined, timeOptions));
      setCurrentMinutes(now.getHours() * 60 + now.getMinutes());
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // -----------------------------
  // Load doctor id
  // -----------------------------
  useEffect(() => {
    (async () => {
      try {
        const d = await getDecryptData();
        setDoctorId(d?._id || null);
      } catch (e) {
        console.error(e);
        setDoctorId(null);
      }
    })();
  }, []);

  // -----------------------------
  // API: queue
  // -----------------------------
  const getUserQuee = async () => {
    try {
      const decryptdatajson = await getDecryptData();
      const id = decryptdatajson?._id;

      const { data } = await axiosInstance.get(`/quee-view/${hospital}/${id}`);
      if (data?.success) setQuee(data);

      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      toast.error("Order not found");
    }
  };

  useEffect(() => {
    getUserQuee();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // ✅ Added messages filtered by doctor (works with your structure)
  // -----------------------------
  const addedMessages = useMemo(() => {
    if (!doctorId) return [];

    return (allMessages || []).filter((msg) => {
      if (msg?.type !== "appointment") return false;
      if (msg?.subType !== "add") return false;
      if (msg?.hosId !== hospital) return false;

      if (!Array.isArray(msg?.addProduct)) return false;

      const idx = getMyProductIndex(msg, doctorId);
      return idx >= 0;
    });
  }, [allMessages, doctorId, hospital]);

  // -----------------------------
  // ✅ removed keys (doctor-wise + productIndex-wise)
  // -----------------------------
  const removedKeys = useMemo(() => {
    return new Set(
      (allMessages || [])
        .filter((m) => m?.type === "appointment" && m?.subType === "remove")
        .map((m) => makeKey(m.orderId, m.doctorId, m.productIndex))
    );
  }, [allMessages]);

  // -----------------------------
  // ✅ API hold/priority sets (read from addProduct[index].subType)
  // -----------------------------
  const apiHoldSet = useMemo(() => {
    const set = new Set();
    if (!doctorId) return set;

    (Quee?.orders || []).forEach((order) => {
      const myIndex = getMyProductIndex(order, doctorId);
      if (myIndex < 0) return;

      const st = order?.addProduct?.[myIndex]?.subType;
      if (st === "hold") set.add(makeKey(order.orderId, doctorId, myIndex));
    });

    return set;
  }, [Quee, doctorId]);

  const apiPrioritySet = useMemo(() => {
    const set = new Set();
    if (!doctorId) return set;

    (Quee?.orders || []).forEach((order) => {
      const myIndex = getMyProductIndex(order, doctorId);
      if (myIndex < 0) return;

      const st = order?.addProduct?.[myIndex]?.subType;
      if (st === "priority") set.add(makeKey(order.orderId, doctorId, myIndex));
    });

    return set;
  }, [Quee, doctorId]);

  // -----------------------------
  // ✅ Live hold/priority based on allMessages (last wins)
  // -----------------------------
  const holdSet = useMemo(() => {
    const set = new Set(apiHoldSet);

    (allMessages || []).forEach((msg) => {
      if (msg?.type !== "appointment") return;
      if (!msg?.orderId) return;

      const k = makeKey(msg.orderId, msg.doctorId, msg.productIndex);
      if (msg.subType === "hold") set.add(k);
      if (msg.subType === "unhold") set.delete(k);
    });

    return set;
  }, [apiHoldSet, allMessages]);

  const prioritySet = useMemo(() => {
    const set = new Set(apiPrioritySet);

    (allMessages || []).forEach((msg) => {
      if (msg?.type !== "appointment") return;
      if (!msg?.orderId) return;

      const k = makeKey(msg.orderId, msg.doctorId, msg.productIndex);
      if (msg.subType === "priority") set.add(k);
      if (msg.subType === "unpriority") set.delete(k);
    });

    return set;
  }, [apiPrioritySet, allMessages]);

  // -----------------------------
  // ✅ FINAL FILTER + SORT (doctor-wise)
  // Each item becomes: { ...order, myIndex, myProduct, myKey }
  // ✅ FIXED: date filter uses myProduct.date first
  // -----------------------------
  const filteredOrders = useMemo(() => {
    if (!doctorId || !Quee?.orders || currentMinutes === null) return [];

    const today = toDateOnly(new Date());

    // merge Quee.orders + addedMessages (by orderId)
    const orderMap = new Map();

    (Quee.orders || []).forEach((order) => {
      if (order?.orderId) orderMap.set(order.orderId, order);
    });

    (addedMessages || []).forEach((msg) => {
      if (msg?.orderId) orderMap.set(msg.orderId, msg);
    });

    const finalOrders = Array.from(orderMap.values());

    return finalOrders
      .map((order) => {
        const myIndex = getMyProductIndex(order, doctorId);
        if (myIndex < 0) return null;

        const myProduct = order?.addProduct?.[myIndex] || null;
        const myKey = makeKey(order.orderId, doctorId, myIndex);

        return { ...order, myIndex, myProduct, myKey };
      })
      .filter(Boolean)
      .filter((order) => {
        // removed doctor-wise
        if (removedKeys.has(order.myKey)) return false;

        // hold doctor-wise
        if (holdSet.has(order.myKey)) return false;

        // ✅ date filter (doctor-wise product date)
        const orderDate = toDateOnly(order?.myProduct?.date || order?.date);
        if (orderDate !== today) return false;

        // time filter (your logic)
        const start = parseTimeToMinutes(order.Ltime);
        const end = parseTimeToMinutes(order.Etime);

        if (start === null && end === null) return true;
        if (start === null || end === null) return false;

        return currentMinutes >= start && currentMinutes <= end;
      })
      .sort((a, b) => {
        const ap = prioritySet.has(a.myKey) ? 1 : 0;
        const bp = prioritySet.has(b.myKey) ? 1 : 0;

        if (ap !== bp) return bp - ap;
        return new Date(a.createdAt) - new Date(b.createdAt);
      });
  }, [Quee, currentMinutes, addedMessages, removedKeys, holdSet, prioritySet, doctorId]);

  // ✅ Hold list (doctor-wise)
  // ✅ FIXED: date filter uses myProduct.date first
  const holdOrders = useMemo(() => {
    if (!doctorId || !Quee?.orders || currentMinutes === null) return [];

    const today = toDateOnly(new Date());

    return (Quee.orders || [])
      .map((order) => {
        const myIndex = getMyProductIndex(order, doctorId);
        if (myIndex < 0) return null;

        const myProduct = order?.addProduct?.[myIndex] || null;
        const myKey = makeKey(order.orderId, doctorId, myIndex);
        return { ...order, myIndex, myProduct, myKey };
      })
      .filter(Boolean)
      .filter((o) => {
        const isHeld = holdSet.has(o.myKey);
        if (!isHeld) return false;

        const orderDate = toDateOnly(o?.myProduct?.date || o?.date);
        if (orderDate !== today) return false;

        const start = parseTimeToMinutes(o.Ltime);
        const end = parseTimeToMinutes(o.Etime);

        if (start === null && end === null) return true;
        if (start === null || end === null) return false;

        return currentMinutes >= start && currentMinutes <= end;
      })
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [Quee, holdSet, currentMinutes, doctorId]);

  // -----------------------------
  // Modals + status update (complete/reject)
  // -----------------------------
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // ✅ store full order (for doctor-wise remove)
  const [pendingOrder, setPendingOrder] = useState(null);

  const [mode, setMode] = useState(""); // online | offline
  const [prescriptions, setPrescriptions] = useState([]);
  const [prescriptionInput, setPrescriptionInput] = useState("");
  const [reject, setReject] = useState(null); // 1 cancel | 0 reschedule
  const [reason, setReason] = useState("");

  const resetCompleteForm = () => {
    setMode("");
    setPrescriptions([]);
    setPrescriptionInput("");
    setPendingOrder(null);
    setReason("");
    setReject(null);
  };

  const addPrescription = () => {
    const val = prescriptionInput.trim();
    if (!val) return;

    if (prescriptions.some((p) => p.toLowerCase() === val.toLowerCase())) {
      setPrescriptionInput("");
      return;
    }

    setPrescriptions((prev) => [...prev, val]);
    setPrescriptionInput("");
  };

  const removePrescription = (idx) => {
    setPrescriptions((prev) => prev.filter((_, i) => i !== idx));
  };

  const onPrescriptionKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addPrescription();
    }
  };

  const handleStatusChange = (order, newStatus) => {
    if (!order?._id) return;

    setPendingOrder(order);

    if (newStatus === 1) {
      setShowCompleteModal(true);
    } else if (newStatus === 2) {
      setShowRejectModal(true);
    }
  };

  const submitComplete = async (mystatus) => {
    if (!pendingOrder?._id) return;

    let mydata = {};

    if (mystatus === 1) {
      if (!mode) return toast.error("Please select mode (Online/Offline)");
      if (prescriptions.length === 0 && mode === "online")
        return toast.error("Please add at least 1 prescription");

      mydata = { status: 1, mode, Prescription: prescriptions };
    }

    if (mystatus === 2) {
      if (reject === null) return toast.error("Please select a rejection option.");
      if (reject === 1 && !reason) return toast.error("Please provide a reason for the rejection.");

      mydata = { status: reject === 1 ? 2 : 3, reason };
    }

    try {
      // ✅ IMPORTANT: send productIndex so backend can update addProduct[index]
      const response = await axiosInstance.put(`/admin/update-appointment/${pendingOrder._id}`, {
        ...mydata,
        productIndex: pendingOrder.myIndex,
      });

      const { success, order } = response.data;

      if (success) {
        // ✅ doctor-wise remove message (orderId + doctorId + productIndex)
        sendMessage({
          orderId: pendingOrder.orderId, // token (N0128)
          type: "appointment",
          subType: "remove",
          doctorId,
          productIndex: pendingOrder.myIndex,
        });

        // ✅ if step moves forward and you want to add next stage to queue
        if (order) {
          if (Array.isArray(order.addProduct) && typeof order.step === "number") {
            if (order.addProduct.length > order.step) {
              sendMessage({
                ...order,
                type: "appointment",
                subType: "add",
              });
            }
          }
        }

        toast.success("Appointment updated!");
        setShowCompleteModal(false);
        setShowRejectModal(false);
        resetCompleteForm();
         await getUserQuee();
         await getUserQuee();
        // window.location.reload();
      }
    } catch (error) {
      console.error("Error completing:", error);
      toast.error("Failed to update appointment");
    }
  };

  // -----------------------------
  // ✅ Hold/Priority API + Toggle (doctor-wise)
  // -----------------------------
  const priortyHold = async (mongoId, mystatus, dId, productIndex) => {
    if (!mongoId || !mystatus) return;

    try {
      await axiosInstance.put(`/admin/update-appointment/${mongoId}`, {
        mystatus,
        productIndex,
      });
    } catch (error) {
      console.error("Error hold/priority:", error);
      toast.error("Failed to update appointment");
    }
  };

  const toggleHold = (order) => {
    if (!order?.orderId || !order?._id || !doctorId) return;

    const key = makeKey(order.orderId, doctorId, order.myIndex);
    const isHeld = holdSet.has(key);

    priortyHold(order._id, isHeld ? "unhold" : "hold", doctorId, order.myIndex);

    sendMessage({
      orderId: order.orderId,
      type: "appointment",
      subType: isHeld ? "unhold" : "hold",
      doctorId,
      productIndex: order.myIndex,
    });

    toast.success(isHeld ? "Removed from Hold" : "Patient put on Hold");
  };

  const togglePriority = (order) => {
    if (!order?.orderId || !order?._id || !doctorId) return;

    const key = makeKey(order.orderId, doctorId, order.myIndex);
    const isPriority = prioritySet.has(key);

    priortyHold(order._id, isPriority ? "unpriority" : "priority", doctorId, order.myIndex);

    sendMessage({
      orderId: order.orderId,
      type: "appointment",
      subType: isPriority ? "unpriority" : "priority",
      doctorId,
      productIndex: order.myIndex,
    });

    toast.success(isPriority ? "Priority removed" : "Marked as Priority");
  };

  const cancelSuggestions = [
    "Doctor unavailable",
    "Patient not available",
    "Emergency case",
    "Schedule conflict",
    "Health issue",
    "Personal reason",
  ];

  // -----------------------------
  // UI derived
  // -----------------------------
  const topOrder = filteredOrders?.[0] ? [filteredOrders[0]] : [];
  const restOrders = filteredOrders?.length > 1 ? filteredOrders.slice(1) : [];
  const holdList = holdOrders || [];

  const getRoomForOrder = (order) => {
    const roomFromProduct =
      order?.myProduct?.employee?.data?.hospitalMeta?.[order?.hosId?._id]?.room;

    const roomFromEmp =
      order?.employeeId?.hospitalMeta?.[order?.hosId?._id]?.room;

    return roomFromProduct || roomFromEmp || "--";
  };

  // ==========================================================
  // ✅ RESCHEDULE FEATURE (NO status change, only date/time)
  // ==========================================================
  const [allSchedule, setAllSchedule] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [AddProduct, setAddProduct] = useState([]);

  const FetchDoctorBookChange = async (myid, hosId) => {
    try {
      setLoadingSchedule(true);
      const response = await axiosInstance.get(`/get-vendor-slot?doc=${myid}&days=10&hos=${hosId}`);
      const { success, schedule } = response.data;
      if (success) setAllSchedule(schedule ?? []);
      return schedule ?? [];
    } catch (error) {
      console.error("Error fetching bookings:", error);
      return [];
    } finally {
      setLoadingSchedule(false);
    }
  };

  function to24Hour(t) {
    if (!t) return "";
    let [time, modifier] = t.trim().split(" ");
    let [hours, minutes] = time.split(":");

    hours = parseInt(hours, 10);

    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  }

  // ✅ ONLY updates addProduct date/time for that productIndex
  const updateDoctorDateTime = async (prodata, item) => {
    try {
      const { data } = await axiosInstance.put(`/admin/update-order-product/${item._id}`, {
        addProduct: prodata,
      });

      if (data?.success) {
        toast.success("Date & time changed successfully!");
        await getUserQuee(); // refresh queue
      }
    } catch (error) {
      console.error("Error updateDoctorDateTime:", error);
      toast.error("something went wrong!");
    }
  };

  // ✅ Reschedule handler (seed from item.addProduct, update index only)
  const handleEmployeeChange = async (index, selectedOption, item) => {
    try {
      if (!item?._id) return toast.error("Order not found");
      if (index === undefined || index === null) return toast.error("Product index missing");

      const employeeId = selectedOption?.value;
      const employee = selectedOption?.data;

      if (!employeeId) return toast.error("Please select an employee");

      const hosId = item?.hosId?._id || item?.hosId;
      const scheduleList = await FetchDoctorBookChange(employeeId, hosId);

      // ✅ seed from this order's addProduct (NOT global AddProduct)
      const baseProducts = Array.isArray(item?.addProduct) ? [...item.addProduct] : [];
      setAddProduct(baseProducts);

      toast.custom(
        (t) => (
          <SchedulePickerToast
            t={t}
            scheduleList={scheduleList}
            employeeId={employeeId}
            employee={employee}
            employeeName={selectedOption?.label}
            onConfirm={({ date, time }) => {
              const updated = [...baseProducts];

              if (!updated[index]) {
                toast.error("Invalid product index");
                return;
              }

              const [start, end] = (time || "").split("-");
              const startTime24 = start ? to24Hour(start) : "";
              const endTime24 = end ? to24Hour(end) : "";

              // ✅ ONLY CHANGE DATE/TIME (no status)
              updated[index] = {
                ...updated[index],
                date,
                time: time || "",
                startTime: startTime24,
                endTime: endTime24,
                employee: selectedOption,
                employeeId,
              };

              setAddProduct(updated);
              updateDoctorDateTime(updated, item);
            }}
          />
        ),
        { duration: Infinity }
      );
    } catch (err) {
      console.error(err);
      toast.error("Reschedule failed");
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      <main className="w-100">
        {/* COMPLETE MODAL */}
        {showCompleteModal && (
          <div
            className="modal fade show"
            style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Complete Appointment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowCompleteModal(false);
                      resetCompleteForm();
                    }}
                  />
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold d-block">Mode</label>
                    <div className="btn-group gap-2" role="group">
                      <button
                        type="button"
                        className={`btn btn-outline-primary rounded-pill shadow-none px-3 ${
                          mode === "online" ? "active opacity-100" : "opacity-75"
                        }`}
                        onClick={() => setMode("online")}
                      >
                        <i className="ri-global-line"></i> Online
                      </button>

                      <button
                        type="button"
                        className={`btn btn-outline-primary shadow-none rounded-pill px-3 ${
                          mode === "offline" ? "active opacity-100" : "opacity-75"
                        }`}
                        onClick={() => setMode("offline")}
                      >
                        <i className="ri-close-circle-line"></i> Offline
                      </button>
                    </div>
                  </div>

                  {mode === "online" && (
                    <div className="mb-2">
                      <label className="form-label fw-semibold pb-2 border-bottom w-100">
                        Prescriptions
                      </label>

                      <ol className="mt-1 ps-1 ms-3">
                        {prescriptions.map((p, idx) => (
                          <li key={idx}>
                            <div className="d-flex align-items-center justify-content-between">
                              <span>{p}</span>
                              <button
                                type="button"
                                className="btn btn-sm btn-link text-danger p-0 ms-2"
                                onClick={() => removePrescription(idx)}
                              >
                                Remove
                              </button>
                            </div>
                          </li>
                        ))}
                      </ol>

                      <div className="input-group">
                        <input
                          className="form-control shadow-none"
                          placeholder='Type & press "Add" (e.g., Paracetamol 600mg)'
                          value={prescriptionInput}
                          onChange={(e) => setPrescriptionInput(e.target.value)}
                          onKeyDown={onPrescriptionKeyDown}
                        />
                        <button type="button" className="btn btn-primary" onClick={addPrescription}>
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCompleteModal(false);
                      resetCompleteForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn text-white btn-success"
                    onClick={() => submitComplete(1)}
                  >
                    Complete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* REJECT MODAL */}
        {showRejectModal && (
          <div
            className="modal fade show"
            style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reject Appointment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowRejectModal(false);
                      resetCompleteForm();
                    }}
                  />
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold d-block">Rejection option</label>
                    <div className="btn-group gap-2" role="group">
                      <button
                        type="button"
                        className={`btn btn-outline-primary rounded-pill shadow-none px-3 ${
                          reject === 1 ? "active opacity-100" : "opacity-75"
                        }`}
                        onClick={() => setReject(1)}
                      >
                        <i className="ri-close-circle-line"></i> Cancel
                      </button>
                    </div>
                  </div>

                  {reject === 1 && (
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Cancel Reason</label>

                      <div className="d-flex flex-wrap gap-2 mb-2">
                        {cancelSuggestions.map((item, i) => (
                          <button
                            key={i}
                            type="button"
                            className="btn btn-sm btn-outline-primary shadow-none rounded-pill"
                            onClick={() => setReason(item)}
                          >
                            {item}
                          </button>
                        ))}
                      </div>

                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Write cancel reason..."
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowRejectModal(false);
                      resetCompleteForm();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn text-white btn-success"
                    onClick={() => submitComplete(2)}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingScreen />
        ) : (
          <>
            <div className="app-shell d-flex flex-column">
              {/* Top Bar */}
              <header className="top-bar py-2 px-3 px-md-4 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="bi bi-heart-pulse-fill text-danger fs-4" />
                  </div>
                  <div>
                    <h5 className="mb-0 fw-semibold text-white">
                      {Quee?.hospital?.username} – Live Queue
                    </h5>
                    {doctorId && <small className="text-white-50">Doctor ID: {doctorId}</small>}
                  </div>
                </div>

                <div className="time-box">
                  <div>{currentDate}</div>
                  <div className="time">{currentTime}</div>
                </div>
              </header>

              {/* Main Content */}
              <main className="flex-grow-1 container-fluid py-3 py-md-4">
                <div className="row g-3 g-lg-4">
                  {/* LEFT */}
                  <div className="col-lg-4">
                    {/* Current Patient Card */}
                    <div className="card card-soft mb-3">
                      {filteredOrders.length === 0 ? (
                        <div className="d-flex align-items-center justify-content-center" style={{ height: "50vh" }}>
                          <p className="text-danger h4">No Patient Left</p>
                        </div>
                      ) : (
                        <>
                          <div className="card-header border-0 d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="mb-0 fw-semibold">Current Patient</h6>
                              <small className="text-muted">In consultation</small>
                            </div>
                            <span className="status-pill status-in-room">IN ROOM</span>
                          </div>

                          <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div>
                                <div className="text-muted small mb-1">TOKEN</div>
                                <div className="token-badge">{filteredOrders[0]?.orderId ?? "--"}</div>
                              </div>
                              <div className="text-end">
                                <div className="text-muted small mb-1">ROOM</div>
                                <div className="fw-semibold fs-5">{getRoomForOrder(filteredOrders[0])}</div>
                              </div>
                            </div>

                            <hr />

                            <div className="mb-2">
                              <div className="text-muted small mb-1">Patient Name</div>
                              <div className="fw-semibold">{filteredOrders[0]?.UserDetails?.[0]?.name ?? "--"}</div>
                            </div>

                            <div className="row mb-3">
                              <div className="col-12">
                                <div className="text-muted small mb-0">Age / Gender</div>
                                <div className="fw-semibold">
                                  {filteredOrders[0]?.UserDetails?.[0]?.age ?? "--"} .{" "}
                                  {filteredOrders[0]?.UserDetails?.[0]?.gender === "1" && "M"}
                                  {filteredOrders[0]?.UserDetails?.[0]?.gender === "2" && "F"}
                                  {filteredOrders[0]?.UserDetails?.[0]?.gender === "3" && "O"}
                                </div>
                              </div>
                            </div>

                            <div className="mb-3">
                              <div className="text-muted small mb-1">Reason for Visit</div>
                              <div>{filteredOrders[0]?.UserDetails?.[0]?.disease ?? "NA"}</div>
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex flex-wrap gap-2">
                              <button
                                className="btn btn-success btn-sm flex-grow-1 text-white"
                                type="button"
                                onClick={() => handleStatusChange(filteredOrders[0], 1)}
                              >
                                <i className="bi bi-check-circle me-1" /> Complete
                              </button>

                              <button
                                className="btn btn-outline-danger btn-sm flex-grow-1"
                                onClick={() => handleStatusChange(filteredOrders[0], 2)}
                                type="button"
                              >
                                <i className="bi bi-x-circle me-1" /> Reject
                              </button>

                              <button
                                className="btn btn-outline-warning btn-sm flex-grow-1"
                                type="button"
                                onClick={() => toggleHold(filteredOrders[0])}
                              >
                                <i className="bi bi-pause-circle me-1" /> Hold
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Next Token Preview */}
                    <div className="card card-soft">
                      <div className="card-body d-flex justify-content-between align-items-center">
                        <div>
                          <div className="text-muted small mb-1">Next in Queue</div>
                          <div className="fw-bold">
                            {filteredOrders[1]?.UserDetails?.[0]?.name ?? "--"} – {filteredOrders[1]?.orderId ?? "--"}
                            {filteredOrders[1] && prioritySet.has(filteredOrders[1]?.myKey) && (
                              <span className="badge text-bg-warning ms-2">PRIORITY</span>
                            )}
                          </div>
                          <div className="small text-muted">
                            {filteredOrders[1]?.UserDetails?.[0]?.age ?? "--"} .{" "}
                            {filteredOrders[1]?.UserDetails?.[0]?.gender === "1" && "M"}
                            {filteredOrders[1]?.UserDetails?.[0]?.gender === "2" && "F"}
                            {filteredOrders[1]?.UserDetails?.[0]?.gender === "3" && "O"}
                          </div>
                        </div>

                        {filteredOrders[1] && (
                          <button
                            className="btn btn-primary"
                            onClick={() => handleStatusChange(filteredOrders[1], 2)}
                            type="button"
                          >
                            <i className="bi bi-megaphone-fill me-1" /> Call Next
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* RIGHT */}
                  <div className="col-lg-8">
                    <div className="card card-soft">
                      <div className="card-header border-0 d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <div>
                          <h6 className="mb-0 fw-semibold">Patient Queue</h6>
                          <small className="text-muted">Doctor-wise queue (matches addProduct.employeeId)</small>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          <span className="badge text-bg-warning">
                            Total: <span>{Quee?.quee?.left ?? 0}</span>
                          </span>
                          <span className="badge text-bg-success">
                            Remaining: <span>{filteredOrders.length}</span>
                          </span>
                          <span className="badge text-bg-secondary">
                            Hold: <span>{holdOrders.length}</span>
                          </span>
                        </div>
                      </div>

                      <div className="card-body p-0">
                        <div className="table-responsive">
                          <table className="table table-queue mb-0 align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 90 }}>Token</th>
                                <th>Patient</th>
                                <th style={{ width: 90 }}>Room</th>
                                <th style={{ width: 140 }}>Status</th>
                                <th style={{ width: 260 }}>Actions</th>
                              </tr>
                            </thead>

                            <tbody>
                              {/* 1) TOP ACTIVE */}
                              {topOrder.map((item, i) => {
                                const isPriority = prioritySet.has(item?.myKey);

                                return (
                                  <tr key={item?.myKey || `top-${i}`}>
                                    <td className="fw-bold">
                                      {item?.orderId ?? "--"}
                                      {isPriority && <span className="badge text-bg-success ms-2">P</span>}
                                    </td>

                                    <td>
                                      <div className="fw-semibold">{item?.UserDetails?.[0]?.name ?? "--"}</div>
                                      <div className="small text-muted">Product Index: {item?.myIndex}</div>
                                    </td>

                                    <td>{getRoomForOrder(item)}</td>

                                    <td>
                                      <span className="status-pill bg-success text-white">Active</span>
                                    </td>

                                    <td>
                                      <div className="d-flex gap-2 flex-wrap">
                                        <button
                                          className="btn btn-sm btn-outline-warning"
                                          onClick={() => toggleHold(item)}
                                          type="button"
                                        >
                                          Hold
                                        </button>

                                        <button
                                          className={`btn btn-sm shadow-none ${
                                            isPriority ? "btn-success text-white" : "btn-outline-success"
                                          }`}
                                          onClick={() => togglePriority(item)}
                                          type="button"
                                        >
                                          <i className="bi bi-lightning-charge-fill me-1" />
                                          Priority
                                        </button>

                                        {/* ✅ RESCHEDULE (NEW) */}
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary shadow-none"
                                          onClick={() =>
                                            handleEmployeeChange(item?.myIndex, item?.myProduct?.employee, item)
                                          }
                                        >
                                          <i className="ri-restart-line"></i> Reschedule
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}

                              {/* 2) HOLD */}
                              {holdList.length > 0 && (
                                <>
                                  <tr>
                                    <td colSpan={5} className="bg-light fw-semibold text-warning py-2">
                                      ON HOLD ({holdList.length})
                                    </td>
                                  </tr>

                                  {holdList.map((item, idx) => {
                                    const isPriority = prioritySet.has(item?.myKey);

                                    return (
                                      <tr key={item?.myKey || `hold-${idx}`}>
                                        <td className="fw-bold">
                                          {item?.orderId ?? "--"}
                                          {isPriority && <span className="badge text-bg-warning ms-2">P</span>}
                                        </td>

                                        <td>
                                          <div className="fw-semibold">{item?.UserDetails?.[0]?.name ?? "--"}</div>
                                          <div className="small text-muted">Product Index: {item?.myIndex}</div>
                                        </td>

                                        <td>{getRoomForOrder(item)}</td>

                                        <td>
                                          <span className="status-pill bg-warning text-dark">Hold</span>
                                        </td>

                                        <td>
                                          <div className="d-flex gap-2 flex-wrap">
                                            <button
                                              className="btn btn-sm btn-warning"
                                              onClick={() => toggleHold(item)}
                                              type="button"
                                            >
                                              Unhold
                                            </button>

                                            <button
                                              className={`btn btn-sm shadow-none ${
                                                isPriority ? "btn-success text-white" : "btn-outline-success"
                                              }`}
                                              onClick={() => togglePriority(item)}
                                              type="button"
                                            >
                                              <i className="bi bi-lightning-charge-fill me-1" />
                                              Priority
                                            </button>

                                            {/* ✅ RESCHEDULE (NEW) */}
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-primary shadow-none"
                                              onClick={() =>
                                                handleEmployeeChange(item?.myIndex, item?.myProduct?.employee, item)
                                              }
                                            >
                                              <i className="ri-restart-line"></i> Reschedule
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </>
                              )}

                              {/* 3) ON GOING */}
                              {restOrders.length > 0 && (
                                <>
                                  <tr>
                                    <td colSpan={5} className="bg-light fw-semibold text-success py-2">
                                      ON GOING ({restOrders.length})
                                    </td>
                                  </tr>

                                  {restOrders.map((item, index) => {
                                    const isPriority = prioritySet.has(item?.myKey);

                                    return (
                                      <tr key={item?.myKey || `rest-${index}`}>
                                        <td className="fw-bold">
                                          {item?.orderId ?? "--"}
                                          {isPriority && <span className="badge text-bg-warning ms-2">P</span>}
                                        </td>

                                        <td>
                                          <div className="fw-semibold">{item?.UserDetails?.[0]?.name ?? "--"}</div>
                                          <div className="small text-muted">Product Index: {item?.myIndex}</div>
                                        </td>

                                        <td>{getRoomForOrder(item)}</td>

                                        <td>
                                          <span
                                            className={`status-pill ${
                                              isPriority ? "bg-warning text-dark" : "status-waiting"
                                            }`}
                                          >
                                            {isPriority ? "Priority" : "Waiting"}
                                          </span>
                                        </td>

                                        <td>
                                          <div className="d-flex gap-2 flex-wrap">
                                            <button
                                              className="btn btn-sm btn-outline-warning"
                                              onClick={() => toggleHold(item)}
                                              type="button"
                                            >
                                              Hold
                                            </button>

                                            <button
                                              className={`btn btn-sm shadow-none ${
                                                isPriority ? "btn-success text-white" : "btn-outline-success"
                                              }`}
                                              onClick={() => togglePriority(item)}
                                              type="button"
                                            >
                                              <i className="bi bi-lightning-charge-fill me-1" />
                                              Priority
                                            </button>

                                            {/* ✅ RESCHEDULE (NEW) */}
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-primary shadow-none"
                                              onClick={() =>
                                                handleEmployeeChange(item?.myIndex, item?.myProduct?.employee, item)
                                              }
                                            >
                                              <i className="ri-restart-line"></i> Reschedule
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </>
                              )}

                              {/* empty */}
                              {filteredOrders.length === 0 && holdList.length === 0 && (
                                <tr>
                                  <td colSpan={5} className="text-center py-4 text-muted">
                                    No records found for this doctor.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </>
        )}
      </main>
    </>
  );
};

export default DoctorQueeView;
